import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLabel,
  VictoryScatter,
  VictoryStack,
} from 'victory';

import type { StackBarChartProps } from './types';

// Default value formatters
const defaultFormatValue = (value: number): string => {
  if (value >= 1000) {
    return Math.round(value / 1000) + 'B';
  }

  return Math.round(value) + 'M';
};

const defaultFormatTotal = (value: number): string => {
  if (value >= 1000) {
    return Math.round(value) + 'M';
  }

  return Math.round(value) + 'M';
};

export function StackBarChart<T extends { date: string }>({
  data,
  series,
  yAxisLabel = '',
  height = 380,
  yTickValues = [0, 50, 100, 150, 200, 250],
  yMax = 250,
  formatValue = defaultFormatValue,
  formatTotal = defaultFormatTotal,
  showBarLabels = true,
  minLabelThreshold = 10,
  totalLabel = 'Total',
}: StackBarChartProps<T>) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [cursorX, setCursorX] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(1200);
  const chartContainerRef = useRef<HTMLDivElement>(null);

  // Track container width for responsive chart
  useEffect(() => {
    const container = chartContainerRef.current;

    if (!container) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);
    setContainerWidth(container.offsetWidth);

    return () => resizeObserver.disconnect();
  }, []);

  // Chart dimensions
  const chartWidth = containerWidth;

  // Responsive padding - match MultiLineChart padding for alignment
  const chartPadding = useMemo(() => {
    if (containerWidth < 600) {
      return { top: 30, bottom: 55, left: 45, right: 50 };
    }

    return { top: 30, bottom: 60, left: 60, right: 65 };
  }, [containerWidth]);

  // Calculate bar width based on available space
  const barWidth = useMemo(() => {
    const availableWidth = chartWidth - chartPadding.left - chartPadding.right;
    // Calculate slot width (space per bar including gaps)
    const slotWidth = availableWidth / (data.length + 1); // +1 for padding space

    // Bar width is ~65% of slot, clamped to reasonable bounds
    return Math.max(18, Math.min(42, slotWidth * 0.65));
  }, [chartWidth, chartPadding.left, chartPadding.right, data.length]);

  // Domain padding: half bar width + margin to keep bars away from axes
  // This value is applied equally to left and right sides
  const xDomainPadding = barWidth / 2 + 20;

  // Prepare stacked bar data for each series
  // Offset x by 0.5 so bars are centered between axis lines, not on them
  const stackedData = useMemo(() => {
    return series.map((s) => ({
      ...s,
      data: data.map((d, index) => ({
        x: index + 0.5,
        y: ((d as Record<string, unknown>)[s.key] as number) || 0,
      })),
    }));
  }, [data, series]);

  // Calculate totals for each bar (with same x offset as stackedData)
  const barTotals = useMemo(() => {
    return data.map((d, index) => {
      const total = series.reduce((sum, s) => {
        return sum + (((d as Record<string, unknown>)[s.key] as number) || 0);
      }, 0);

      return { x: index + 0.5, y: total };
    });
  }, [data, series]);

  // Get tick values for x-axis (offset by 0.5 to align with bars)
  const xTickValues = useMemo(() => {
    const values: number[] = [];
    const dataLength = data.length;

    let tickInterval: number;

    if (containerWidth < 500) {
      tickInterval = 4;
    } else if (containerWidth < 700) {
      tickInterval = 3;
    } else if (containerWidth < 900) {
      tickInterval = 2;
    } else {
      tickInterval = 1;
    }

    for (let i = 0; i < dataLength; i += tickInterval) {
      values.push(i + 0.5);
    }

    const lastValue = dataLength - 1 + 0.5;

    if (values[values.length - 1] !== lastValue && tickInterval > 1) {
      values.push(lastValue);
    }

    return values;
  }, [containerWidth, data.length]);

  const formatXTick = (tickValue: number) => {
    const index = Math.round(tickValue - 0.5);

    if (index >= 0 && index < data.length) {
      return data[index].date;
    }

    return '';
  };

  // Get values at hovered index
  const hoveredData = useMemo(() => {
    if (hoveredIndex === null) {
      return null;
    }

    const dataPoint = data[hoveredIndex];
    const dataPointRecord = dataPoint as Record<string, unknown>;

    const values = series.map((s) => ({
      key: s.key,
      label: s.label,
      color: s.color,
      value: (dataPointRecord[s.key] as number) || 0,
    }));

    const total = values.reduce((sum, v) => sum + v.value, 0);

    return {
      date: dataPoint.date,
      values: values.reverse(), // Reverse to show top-to-bottom in tooltip
      total,
    };
  }, [hoveredIndex, data, series]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const container = chartContainerRef.current;

      if (!container) {
        return;
      }

      const svg = container.querySelector('svg');

      if (!svg) {
        return;
      }

      const point = svg.createSVGPoint();

      point.x = e.clientX;
      point.y = e.clientY;

      const ctm = svg.getScreenCTM();

      if (!ctm) {
        return;
      }

      const svgPoint = point.matrixTransform(ctm.inverse());
      const svgX = svgPoint.x;

      const chartAreaLeft = chartPadding.left;
      const chartAreaRight = chartWidth - chartPadding.right;
      const chartAreaWidth = chartAreaRight - chartAreaLeft;

      if (svgX < chartAreaLeft || svgX > chartAreaRight) {
        setHoveredIndex(null);

        return;
      }

      const containerRect = container.getBoundingClientRect();

      setCursorX(e.clientX - containerRect.left);

      const relativeX = svgX - chartAreaLeft;
      const dataLength = data.length;
      const barSpacing = chartAreaWidth / dataLength;
      // Account for bars being offset by 0.5 in data space
      const index = Math.floor(relativeX / barSpacing);
      const clampedIndex = Math.max(0, Math.min(dataLength - 1, index));

      setHoveredIndex(clampedIndex);
    },
    [chartPadding.left, chartPadding.right, chartWidth, data.length],
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null);
  }, []);

  return (
    <div className="rounded-sm border px-md py-lg bg-level-02 border-emphasis-subtle sm:px-xl md:px-2xl">
      {/* Legend */}
      <div className="mb-sm flex flex-wrap items-center justify-center gap-md sm:gap-lg md:gap-xl">
        {[...series].reverse().map((s) => (
          <div key={s.key} className="flex items-center gap-sm">
            <div
              className="h-md w-md rounded-sm"
              style={{ backgroundColor: s.color }}
            />
            <span className="text-body-sm text-subtle">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div
        ref={chartContainerRef}
        className="relative cursor-crosshair"
        style={{ height }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        data-testid="chart-container"
      >
        <VictoryChart
          width={chartWidth}
          height={height}
          padding={chartPadding}
          domainPadding={{ x: [xDomainPadding, xDomainPadding * 0.15], y: 20 }}
          domain={{ x: [0, data.length], y: [0, yMax] }}
        >
          {/* Y-Axis */}
          <VictoryAxis
            dependentAxis
            crossAxis={false}
            tickValues={yTickValues}
            tickFormat={(t: number) => `${t}M`}
            style={{
              axis: { stroke: '#FFFFFF12' },
              grid: { stroke: '#FFFFFF12' },
              tickLabels: {
                fill: '#878787',
                fontSize: 12,
                fontFamily: 'SF Pro Text, sans-serif',
              },
              axisLabel: {
                fill: '#878787',
                fontSize: 14,
                fontFamily: 'SF Pro Text, sans-serif',
                padding: 45,
              },
            }}
            label={yAxisLabel}
          />

          {/* X-Axis */}
          <VictoryAxis
            tickValues={xTickValues}
            tickFormat={formatXTick}
            style={{
              axis: { stroke: '#FFFFFF12' },
              tickLabels: {
                fill: '#878787',
                fontSize: 10,
                fontFamily: 'SF Pro Text, sans-serif',
                angle: -45,
                textAnchor: 'end',
              },
            }}
          />

          {/* Stacked Bars */}
          <VictoryStack>
            {stackedData.map((s, _seriesIndex) => (
              <VictoryBar
                key={s.key}
                data={s.data}
                barWidth={barWidth}
                style={{
                  data: {
                    fill: s.color,
                    opacity: ({ datum }: { datum?: { x: number } }) =>
                      hoveredIndex !== null && hoveredIndex + 0.5 !== datum?.x
                        ? 0.5
                        : 1,
                  },
                }}
                labels={({ datum }: { datum: { y: number; x: number } }) => {
                  if (!showBarLabels) {
                    return '';
                  }

                  if (datum.y < minLabelThreshold) {
                    return '';
                  }

                  return formatValue(datum.y);
                }}
                labelComponent={
                  <VictoryLabel
                    dy={(args: unknown) => {
                      const { datum, scale } = args as {
                        datum?: { y: number; y0?: number };
                        scale?: { y: (val: number) => number };
                      };

                      if (!datum || !scale) {
                        return 0;
                      }

                      // Calculate center of the bar segment
                      const y0 = datum.y0 ?? 0;
                      const yTop = scale.y(datum.y);
                      const yBottom = scale.y(y0);

                      // Move label down by half the segment height
                      return (yBottom - yTop) / 2;
                    }}
                    style={{
                      fill: '#FFFFFF',
                      fontSize: 10,
                      fontFamily: 'SF Pro Text, sans-serif',
                      fontWeight: 500,
                    }}
                    verticalAnchor="middle"
                  />
                }
              />
            ))}
          </VictoryStack>

          {/* Total labels at top of each bar */}
          <VictoryScatter
            data={barTotals}
            size={0}
            labels={({ datum }: { datum: { y: number } }) =>
              formatTotal(datum.y)
            }
            labelComponent={
              <VictoryLabel
                dy={-12}
                style={{
                  fill: '#FFFFFF',
                  fontSize: 11,
                  fontFamily: 'SF Pro Text, sans-serif',
                  fontWeight: 600,
                }}
                textAnchor="middle"
              />
            }
          />
        </VictoryChart>

        {/* Tooltip */}
        {hoveredIndex !== null && hoveredData ? (
          <div
            className="pointer-events-none absolute top-[20px] z-10 rounded-md border px-md py-sm shadow-lg bg-level-03 border-emphasis-subtle"
            style={
              hoveredIndex > data.length / 2
                ? { right: `calc(100% - ${cursorX}px + 12px)` }
                : { left: cursorX + 12 }
            }
          >
            <div className="mb-xs border-b pb-xs text-[11px] font-semibold border-emphasis-subtle text-default">
              {hoveredData.date}
            </div>
            <div className="flex flex-col gap-[3px]">
              {hoveredData.values.map((item) => (
                <div
                  key={item.key}
                  className="flex items-center justify-between gap-lg text-[11px]"
                >
                  <div className="flex items-center gap-xs">
                    <div
                      className="h-[6px] w-[6px] rounded-sm"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-subtle">{item.label}</span>
                  </div>
                  <span className="font-medium tabular-nums text-default">
                    {formatValue(item.value)}
                  </span>
                </div>
              ))}
              <div className="mt-xs flex items-center justify-between gap-lg border-t pt-xs text-[11px] border-emphasis-subtle">
                <span className="font-semibold text-default">{totalLabel}</span>
                <span className="font-semibold tabular-nums text-default">
                  {formatTotal(hoveredData.total)}
                </span>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

StackBarChart.displayName = 'StackBarChart';
